import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Text,
  Flex,
  IconButton,
  VStack,
  Image,
  HStack,
  useColorMode,
  useBreakpointValue,
  Tooltip,
  useToast,
  // no extra imports
} from "@chakra-ui/react";
import {
  ArrowBackIcon,
  ArrowForwardIcon,
  StarIcon,
  DeleteIcon,
} from "@chakra-ui/icons";
import { FaRegHeart, FaHeart } from "react-icons/fa";
import axiosInstance from "../../helpers/axiosInstance";
import Translate from "../../components/LanguageTranslation/Translate";
import ExternalLinkButton from "../ExternalLinkButton";
import { useCurrency } from "../CurrencyExchange/CurrencyContext";

const Activities = ({
  data = [],
  category = "explore",
  conversationId,
  cityName,
  onActivitiesChange,
  itineraryTabs,
  fromItineray,
  fromChatItineray,
  tripPlan,
  dayIndex,
}) => {
  const [likedActivities, setLikedActivities] = useState({});
  const [current, setCurrent] = useState(0);
  const [isDeletingAll, setIsDeletingAll] = useState(false);
  const [isDeleting, setIsDeleting] = useState({});
  const toast = useToast();

  const { convertCurrency, currency } = useCurrency();
  const { colorMode } = useColorMode();

  const numVisibleCards = useBreakpointValue({ base: 1, md: 2, lg: 2 });
  const numTotalCards = data.length;

  const containerRef = useRef(null);
  const touchStartXRef = useRef(null);

  // show only on first card, then hide after 3s
  const [showCallout, setShowCallout] = useState(true);
  useEffect(() => {
    const timer = setTimeout(() => setShowCallout(false), 9000);
    return () => clearTimeout(timer);
  }, []); // run once on mount

  const next = () => {
    setCurrent((prev) => (prev + numVisibleCards) % numTotalCards);
  };

  const prev = () => {
    setCurrent(
      (prev) => (prev - numVisibleCards + numTotalCards) % numTotalCards
    );
  };

  const handleTouchStart = (e) => {
    touchStartXRef.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e) => {
    const touchEndX = e.changedTouches[0].clientX;
    const diff = touchStartXRef.current - touchEndX;
    if (diff > 50) next();
    else if (diff < -50) prev();
  };

  const isActivityLiked = (idx, item) =>
    likedActivities[idx] ?? item.added_to_bucket_list ?? false;

  const sendLikedActivity = async (activity, added) => {
    const payload = {
      conversation_data: activity,
      // category: category,
      conversation_type: "explore_conversation",
      added_to_bucket_list: added,
    };
    try {
      await axiosInstance.post(
        `/chat/conversation/update-conversation-heart-icon/${conversationId}`,
        payload
      );
      // Call the second endpoint only when activity is liked
      if (added) {
        const bucketPayload = {
          data: activity,
          category: category,
          conversation_type: "explore_conversation",
        };
        await axiosInstance.post(
          "/bucket-list/save-third-party-service",
          bucketPayload
        );
      }
    } catch (error) {
      console.error("Error saving liked activity:", error);
    }
  };

  const toggleLike = (index, activity) => {
    const currentlyLiked = isActivityLiked(index, activity);
    const newLiked = !currentlyLiked; // white → red  OR  red → white

    setLikedActivities((prev) => ({ ...prev, [index]: newLiked }));
    sendLikedActivity(activity, newLiked); // <-- pass TRUE or FALSE
  };

  const hasPrev = current > 0;
  const hasNext = current + numVisibleCards < numTotalCards;

  // Delete a specific activity
  const handleDeleteActivity = async (activityIndex) => {
    if (!conversationId || !cityName) return;
    // console.log(
    //   "Deleting activity at index:",
    //   activityIndex,
    //   "for day:",
    //   dayIndex
    // );
    setIsDeleting((prev) => ({ ...prev, [activityIndex]: true }));
    try {
      const endpoint = itineraryTabs
        ? `/chat/conversation/update-trip-plan/${conversationId}`
        : `/chat/conversation/delete-activities-info/${conversationId}`;

      if (itineraryTabs) {
        // Find the day and index in the trip plan
        let updatedTripPlan = JSON.parse(JSON.stringify(tripPlan)); // deep copy
        let found = false;

        // Use the dayIndex to directly access the correct day
        if (dayIndex !== undefined && updatedTripPlan.itinerary[dayIndex]) {
          const day = updatedTripPlan.itinerary[dayIndex];
          if (
            Array.isArray(day.activity_detail_list) &&
            day.activity_detail_list.length > 0
          ) {
            // activityIndex is the index within the current day's activity list
            if (activityIndex < day.activity_detail_list.length) {
              // Remove from activity_detail_list and activity_name_list
              day.activity_detail_list.splice(activityIndex, 1);
              if (Array.isArray(day.activity_name_list)) {
                day.activity_name_list.splice(activityIndex, 1);
              }
              found = true;
            }
          }
        }

        // console.log("Updated trip plan:", updatedTripPlan);
        const response = await axiosInstance.post(endpoint, {
          city: cityName,
          trip_plan: updatedTripPlan,
        });

        // Check if the deletion was successful
        if (response.data && response.data.success) {
          // Update the frontend UI by calling the parent's onActivitiesChange
          if (onActivitiesChange) onActivitiesChange(activityIndex, "single");
          // Also update the local tripPlan state if needed
          // The parent component should handle the UI update
        } else {
          // Show error toast if deletion failed
          toast({
            title: "Error",
            description:
              response.data?.message ||
              response.data?.data?.message ||
              "Failed to delete activity",
            status: "error",
            duration: 3000,
            isClosable: true,
          });
        }
      } else {
        // For delete-activities-info, use DELETE as before
        const response = await axiosInstance.delete(endpoint, {
          data: {
            city_name: cityName,
            activity_index: activityIndex,
          },
        });

        // Check if the deletion was successful
        if (response.data && response.data.success) {
          if (onActivitiesChange) onActivitiesChange(activityIndex, "single");
        } else {
          toast({
            title: "Error",
            description:
              response.data?.message ||
              response.data?.data?.message ||
              "Failed to delete activity",
            status: "error",
            duration: 3000,
            isClosable: true,
          });
        }
      }
    } catch (error) {
      console.error("Error deleting activity:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.data?.message ||
        error.message ||
        "Failed to delete activity. Please try again.";
      toast({
        title: "Error",
        description: errorMessage,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsDeleting((prev) => ({ ...prev, [activityIndex]: false }));
    }
  };

  return (
    <Flex
      position="relative"
      alignItems="center"
      justifyContent="center"
      padding={0}
      h="auto"
      w="100%"
      mt={3}
      flexDirection="column"
    >
      {/* Remove heading and delete-all icon from here, only keep the activity cards and navigation */}
      <Flex w="full" alignItems="center" justifyContent="center">
        {hasPrev && (
          <IconButton
            icon={<ArrowBackIcon />}
            isRound
            size="md"
            onClick={prev}
            position="absolute"
            left={{ base: "1px", md: "10px" }}
            backgroundColor="white"
            boxShadow="base"
            zIndex="overlay"
          />
        )}
        <HStack
          ref={containerRef}
          overflow="hidden"
          width="full"
          spacing={4}
          justifyContent={{ base: "center", md: "space-between" }}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          {data.slice(current, current + numVisibleCards).map((item, index) => {
            const convertedPrice = item.pricing?.summary?.fromPrice
              ? convertCurrency(
                  item.pricing.summary.fromPrice,
                  item.pricing.currency
                )
              : "N/A";
            const actualIndex = current + index;
            const activityIndexInDay = current + index; // This is the correct index within the day's activities
            const liked = isActivityLiked(actualIndex, item);

            return (
              <Box
                as="a"
                target="_blank"
                rel="noopener noreferrer"
                key={actualIndex}
                href={item.productUrl}
                width={{ base: "80%", md: "50%", lg: "50%" }}
                // onClick={() =>
                //   onActivityClick &&
                //   onActivityClick(
                //     item.attractionLatitude,
                //     item.attractionLongitude
                //   )
                // }
                _hover={{ cursor: "pointer" }}
              >
                <VStack
                  padding={2}
                  spacing={0}
                  borderRadius="xl"
                  width="100%"
                  display="flex"
                  flexDirection="column"
                  bg="blackAlpha.50"
                  height="100%"
                  position="relative"
                >
                  <Image
                    src={item.images?.[0]?.variants?.[2]?.url}
                    fallbackSrc="https://via.placeholder.com/300x200.png?text=No+Image"
                    alt={item.title}
                    borderTopRadius="xl"
                    objectFit="cover"
                    width="100%"
                    height="23vh"
                  />
                  {/* Delete and Heart icons */}

                  <Box
                    position="absolute"
                    top="3px"
                    right="3px"
                    display="flex"
                    flexDirection="row"
                    alignItems="center"
                    gap="0px"
                  >
                    {fromChatItineray && (
                      <Tooltip label="Delete Activity">
                        <IconButton
                          aria-label="Delete activity"
                          icon={<DeleteIcon />}
                          size="md"
                          colorScheme="red"
                          isLoading={isDeleting[activityIndexInDay]}
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            handleDeleteActivity(activityIndexInDay);
                          }}
                          borderRadius="full"
                          bg="none"
                          _hover={{ bg: "none" }}
                        />
                      </Tooltip>
                    )}
                    {!fromItineray && !fromChatItineray && (
                      <Tooltip
                        label={
                          liked
                            ? "Remove from bucket list"
                            : "Add to bucket list"
                        }
                      >
                        <IconButton
                          aria-label={
                            liked
                              ? "Remove from bucket list"
                              : "Add to bucket list"
                          }
                          icon={liked ? <FaHeart /> : <FaRegHeart />}
                          size="md"
                          color={liked ? "red.500" : "white"}
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            toggleLike(actualIndex, item);
                          }}
                          borderRadius="full"
                          bg="none"
                          _hover={{ bg: "none" }}
                          marginLeft="-12px"
                        />
                      </Tooltip>
                    )}
                  </Box>

                  <VStack
                    spacing={1}
                    alignItems="start"
                    p={3}
                    bg="white"
                    w="100%"
                    h="40%"
                    justifyContent="space-between"
                    borderBottomRadius="xl"
                  >
                    <Text
                      fontWeight="bold"
                      fontSize="sm"
                      mb={0}
                      overflow="hidden"
                      w="100%"
                      noOfLines={1}
                      sx={{
                        display: "-webkit-box",
                        WebkitLineClamp: "1",
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      <Translate text={item.title} />
                    </Text>
                    <HStack>
                      <HStack spacing={2} mb={0}>
                        {item.reviews ? (
                          <>
                            <StarIcon boxSize={3} color="#3A4135" mb={0} />
                            <Text mb={0} fontSize="sm">
                              {item.reviews?.combinedAverageRating?.toFixed(2)},
                            </Text>
                          </>
                        ) : (
                          <Text fontSize="sm" mb={0}>
                            <Translate text="N/A" />
                          </Text>
                        )}
                      </HStack>
                      <HStack spacing={2} mb={0}>
                        <Text mb={0} fontSize="sm" fontWeight="bold">
                          {convertedPrice === "N/A"
                            ? ""
                            : `${currency} ${convertedPrice}`}
                        </Text>
                      </HStack>
                    </HStack>
                    {item.productUrl && (
                      <ExternalLinkButton
                        as="a"
                        href={item.productUrl}
                        onClick={(e) => {
                          e.stopPropagation();
                        }}
                        rel="noopener noreferrer"
                        size="sm"
                        bg="#286328"
                        _hover="none"
                        _active="none"
                        textColor="white"
                        width="full"
                        mt={2}
                      >
                        <Translate text="Open" />
                      </ExternalLinkButton>
                    )}
                  </VStack>
                </VStack>
              </Box>
            );
          })}
        </HStack>

        {hasNext && (
          <IconButton
            icon={<ArrowForwardIcon />}
            isRound
            size="md"
            onClick={next}
            position="absolute"
            right={{ base: "1px", md: "10px" }}
            backgroundColor="white"
            boxShadow="base"
            zIndex="overlay"
          />
        )}
      </Flex>
    </Flex>
  );
};

export default Activities;
